export type ClaimStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PARTIALLY_APPROVED'
  | 'REJECTED' | 'REIMBURSED' | 'CANCELLED'

export type TravelStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'CANCELLED'

export type ReimbursementMethod = 'PAYROLL' | 'DIRECT'

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved in full',
  PARTIALLY_APPROVED: 'Approved for less than claimed',
  REJECTED: 'Rejected',
  REIMBURSED: 'Paid',
  CANCELLED: 'Cancelled',
}

export const TRAVEL_STATUS_LABELS: Record<TravelStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Awaiting approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

/** Two different money movements, with different approvals behind them. */
export const REIMBURSEMENT_METHOD_LABELS: Record<ReimbursementMethod, string> = {
  PAYROLL: 'Through payroll, on the next run',
  DIRECT: 'Paid directly, outside payroll',
}

export interface ExpenseTypeView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  description: string | null
  requiresReceipt: boolean
  glAccountCode: string | null
  reimbursementElementId: string | null
  active: boolean
}

export interface ExpenseLimitView {
  id: string
  legalEntityId: string | null
  expenseTypeId: string
  expenseTypeName: string | null
  gradeId: string | null
  gradeName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  maximumPerClaim: number | null
  maximumPerMonth: number | null
  currency: string
  notes: string | null
}

export interface ExpenseClaimLineView {
  id: string
  expenseTypeId: string
  expenseTypeName: string | null
  spentOn: string
  description: string | null
  amountClaimed: number
  amountApproved: number | null
  currency: string
  receiptFileId: string | null
  /** The limit in force when this was claimed, snapshotted. */
  limitApplied: number | null
  /** How far over it went. Warned, never blocked. */
  overLimitBy: number | null
}

export interface ExpenseClaimView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  travelRequestId: string | null
  reference: string
  title: string
  claimDate: string
  currency: string
  totalClaimed: number
  totalApproved: number
  status: string
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionComment: string | null
  reimbursementMethod: string | null
  reimbursedOn: string | null
  payrollRunId: string | null
  lines: ExpenseClaimLineView[]
  overLimitLineCount: number
}

export interface PerDiemRateView {
  id: string
  legalEntityId: string | null
  destination: string
  countryCode: string | null
  gradeId: string | null
  gradeName: string | null
  effectiveFrom: string
  effectiveTo: string | null
  dailyAmount: number
  currency: string
  includesAccommodation: boolean
  notes: string | null
}

export interface TravelRequestView {
  id: string
  employeeId: string
  employeeName: string | null
  reference: string
  destination: string
  countryCode: string | null
  purpose: string | null
  departsOn: string
  returnsOn: string
  daysAway: number
  perDiemRateId: string | null
  perDiemDays: number | null
  perDiemAmount: number | null
  advanceRequested: number | null
  advancePaid: number | null
  currency: string | null
  status: string
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: string | null
  decisionComment: string | null
  /** Set when no rate covers the destination — the advance is entered rather than invented. */
  perDiemUnavailableReason: string | null
}
